import React, { useState, useEffect } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { levelDisplayName } from "@/lib/levelLabels";

const DAYS = [0, 1, 2, 3, 4, 5, 6];

const timetableCopy = {
  en: {
    days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    subtitle: "Weekly period grid for each section",
    section: "Section",
    period: "Period",
    addSlot: "Add slot",
    subject: "Subject",
    teacher: "Teacher",
    start: "Start",
    end: "End",
    saving: "Saving...",
    saveSlot: "Save slot",
    added: "Slot added",
    removed: "Slot removed",
    retry: "Retry",
    loadError: "Unable to load this timetable.",
    invalidTime: "End time must be after start time.",
    unassigned: "Unassigned",
    noSubject: "No subject",
    ageBands: ["0 to 1", "1 to 2", "2 to 3", "3 to 4"],
    ageLabel: "Age",
  },
  ar: {
    days: ["الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت", "الأحد"],
    subtitle: "شبكة الحصص الأسبوعية لكل شعبة",
    section: "الشعبة",
    period: "الحصة",
    addSlot: "إضافة حصة",
    subject: "المادة",
    teacher: "المعلم",
    start: "البداية",
    end: "النهاية",
    saving: "جارٍ الحفظ...",
    saveSlot: "حفظ الحصة",
    added: "تمت إضافة الحصة",
    removed: "تمت إزالة الحصة",
    retry: "إعادة المحاولة",
    loadError: "تعذر تحميل الجدول الدراسي.",
    invalidTime: "يجب أن يكون وقت الانتهاء بعد وقت البدء.",
    unassigned: "غير مسند",
    noSubject: "لا توجد مادة",
    ageBands: ["من 0 إلى 1", "من 1 إلى 2", "من 2 إلى 3", "من 3 إلى 4"],
    ageLabel: "العمر",
  },
};

const PERIOD_COLORS = [
  "bg-emerald-50 border-emerald-200",
  "bg-sky-50 border-sky-200",
  "bg-amber-50 border-amber-200",
  "bg-rose-50 border-rose-200",
  "bg-violet-50 border-violet-200",
  "bg-teal-50 border-teal-200",
  "bg-orange-50 border-orange-200",
  "bg-indigo-50 border-indigo-200",
];

export default function Timetable() {
  const { language, t } = useI18n();
  const copy = timetableCopy[language] || timetableCopy.en;
  const { data: sections } = useQuery({ queryKey: ["sections"], queryFn: async () => unwrap(await api.get("/sections")) });
  const { data: grades } = useQuery({ queryKey: ["grades"], queryFn: async () => unwrap(await api.get("/grades")) });
  const { data: subjects } = useQuery({ queryKey: ["subjects"], queryFn: async () => unwrap(await api.get("/subjects")) });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => unwrap(await api.get("/staff")) });
  const [sectionId, setSectionId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addContext, setAddContext] = useState({ day: 0, period: 1 });
  const qc = useQueryClient();

  const sectionGroups = React.useMemo(() => {
    const groups = copy.ageBands.map((label) => ({ label, sections: [] }));
    for (const section of sections || []) {
      const grade = (grades || []).find((g) => g.id === section.grade_id);
      const order = Number(grade?.display_order);
      if (Number.isInteger(order) && order >= 1 && order <= groups.length) groups[order - 1].sections.push(section);
    }
    return groups.filter((group) => group.sections.length);
  }, [copy, sections, grades]);

  useEffect(() => {
    const visibleIds = sectionGroups.flatMap((group) => group.sections.map((section) => section.id));
    if (visibleIds.length) setSectionId((id) => visibleIds.includes(id) ? id : visibleIds[0]);
  }, [sectionGroups]);

  const { data: slots, isLoading, isError, refetch } = useQuery({
    queryKey: ["timetable", sectionId],
    queryFn: async () => unwrap(await api.get("/timetable", { params: { section_id: sectionId } })),
    enabled: !!sectionId,
  });

  const slotMap = React.useMemo(() => {
    const m = {};
    for (const s of slots || []) m[`${s.day_of_week}-${s.period_number}`] = s;
    return m;
  }, [slots]);

  const periods = React.useMemo(() => {
    const highestPeriod = Math.max(8, ...(slots || []).map((slot) => slot.period_number));
    return Array.from({ length: highestPeriod }, (_, index) => index + 1);
  }, [slots]);

  const teachers = (staff || []).filter((u) => (u.role_assignments || []).some((r) => r.role === "teacher"));

  const createMut = useMutation({
    mutationFn: async (payload) => unwrap(await api.post("/timetable/slots", payload)),
    onSuccess: () => { toast.success(copy.added); qc.invalidateQueries({ queryKey: ["timetable", sectionId] }); setAddOpen(false); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => unwrap(await api.delete(`/timetable/slots/${id}`)),
    onSuccess: () => { toast.success(copy.removed); qc.invalidateQueries({ queryKey: ["timetable", sectionId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openAdd = (day, period) => {
    setAddContext({ day, period });
    setAddOpen(true);
  };

  return (
    <div className="p-6 lg:p-8" data-testid="timetable-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">{t("management.timetableTitle")}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{copy.subtitle}</p>
        </div>
      </div>

      <div className="mb-5" data-testid="section-chips">
        <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">{copy.section}</Label>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 sm:grid-cols-4">
          {sectionGroups.flatMap((group) => group.sections.map((s) => {
            const g = (grades || []).find((grade) => grade.id === s.grade_id);
            const selected = sectionId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSectionId(s.id)}
                aria-pressed={selected}
                data-testid={`section-chip-${s.id}`}
                className={cn(
                  "w-full min-h-[54px] justify-center rounded-full border px-3 py-2 text-sm font-medium transition-colors",
                  selected
                    ? "border-[var(--accent)] bg-[var(--accent)] text-white shadow-sm"
                    : "border-[var(--subtle)] bg-white text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)]",
                )}
              >
                  <span className="flex flex-col items-center text-center leading-tight">
                    <span className="text-[11px] uppercase tracking-wide opacity-80">{copy.ageLabel} {group.label}</span>
                    <span>{levelDisplayName(g, language)} · {s.name}</span>
                  </span>
              </button>
            );
          }))}
        </div>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? <Skeleton className="h-96" /> : isError ? (
            <div className="min-h-64 flex flex-col items-center justify-center gap-3 px-6 text-center" role="alert" data-testid="timetable-load-error">
              <p className="text-sm text-[var(--text-secondary)]">{copy.loadError}</p>
              <Button variant="outline" onClick={() => refetch()} data-testid="retry-timetable">{copy.retry}</Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--subtle)]">
                  <th className="text-start px-3 py-3 uppercase text-[11px] tracking-widest text-[var(--text-muted)] font-semibold w-16">{copy.period}</th>
                  {DAYS.map((day) => (
                    <th key={day} className="text-start px-3 py-3 uppercase text-[11px] tracking-widest text-[var(--text-muted)] font-semibold">{copy.days[day]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p} className="border-b border-[var(--subtle)]">
                    <td className="px-3 py-3 font-mono-num text-[var(--text-secondary)] align-top">
                      <div className="font-bold">{p}</div>
                    </td>
                    {DAYS.map((day) => {
                      const slot = slotMap[`${day}-${p}`];
                      const color = PERIOD_COLORS[(day + p) % PERIOD_COLORS.length];
                      return (
                        <td key={day} className="px-2 py-2 align-top" data-testid={`cell-${day}-${p}`}>
                          {slot ? (
                            <div className={cn("rounded-md border p-2.5 group relative min-h-[64px]", color)}>
                              <div className="text-xs font-semibold text-[var(--text-primary)] leading-tight">{slot.subject?.name || copy.noSubject}</div>
                              <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{slot.start_time} → {slot.end_time}</div>
                              <div className="text-[10px] text-[var(--text-secondary)] mt-0.5 truncate">{slot.teacher?.full_name || copy.unassigned}</div>
                              <button
                                onClick={() => deleteMut.mutate(slot.id)}
                                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-[var(--error)] hover:bg-white rounded p-0.5"
                                data-testid={`delete-slot-${slot.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => openAdd(day, p)}
                              data-testid={`add-slot-${day}-${p}`}
                              className="w-full min-h-[64px] rounded-md border border-dashed border-[var(--subtle)] hover:border-[var(--accent)] hover:bg-[var(--bg)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors flex items-center justify-center text-xs"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{copy.addSlot} · {copy.days[addContext.day]} · {copy.period} {addContext.period}</DialogTitle></DialogHeader>
          <SlotForm
            copy={copy}
            teachers={teachers}
            subjects={subjects || []}
            onSubmit={(f) => createMut.mutate({
              section_id: sectionId,
              day_of_week: addContext.day,
              period_number: addContext.period,
              start_time: f.start_time,
              end_time: f.end_time,
              subject_id: f.subject_id,
              teacher_user_id: f.teacher_user_id || null,
            })}
            loading={createMut.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SlotForm({ copy, teachers, subjects, onSubmit, loading }) {
  const [form, setForm] = useState({ subject_id: "", teacher_user_id: "", start_time: "09:00", end_time: "09:45" });
  const submit = () => {
    if (form.end_time <= form.start_time) {
      toast.error(copy.invalidTime);
      return;
    }
    onSubmit(form);
  };
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase">{copy.subject}</Label>
        <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
          <SelectTrigger className="mt-1" data-testid="slot-subject"><SelectValue placeholder={copy.subject} /></SelectTrigger>
          <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase">{copy.teacher}</Label>
        <Select value={form.teacher_user_id} onValueChange={(v) => setForm({ ...form, teacher_user_id: v })}>
          <SelectTrigger className="mt-1" data-testid="slot-teacher"><SelectValue placeholder={copy.teacher} /></SelectTrigger>
          <SelectContent>{teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase">{copy.start}</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs uppercase">{copy.end}</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" /></div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={!form.subject_id || loading} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="submit-slot">
          {loading ? copy.saving : copy.saveSlot}
        </Button>
      </DialogFooter>
    </div>
  );
}
